/**
 * RBAC store (SC-439): what the signed-in person may do in their company, from
 * `GET /api/rbac/me`, plus the permission catalogue from `GET /api/rbac/catalogue`.
 *
 * This only decides what the UI shows. The server and cloud check every action themselves, so a
 * wrong answer here costs a hidden button, never access.
 *
 * Kept free of imports that lead back to `authService`, which clears this store on sign-out.
 */
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import Parse from 'parse'
import {
  fetchCatalogue,
  fetchMyAccess,
  isRbacApiError,
  type RbacCatalogue,
  type RbacMe,
} from '../services/rbacService'

/**
 * - idle: nothing asked yet.
 * - loading: the first answer for this company is in flight.
 * - ready: `me` holds an answer (a later failed refresh keeps it).
 * - none: there is nothing to ask (no company, or not a member of it): no permissions.
 * - error: the first request failed, so there is no answer: no permissions, fail closed.
 */
export type RbacStatus = 'idle' | 'loading' | 'ready' | 'none' | 'error'

export interface RbacState {
  status: RbacStatus
  /** Whose answer `me` is; an answer for another user is never applied. */
  userId: string | null
  companyId: string | null
  me: RbacMe | null
  catalogue: RbacCatalogue | null
  lastFetchedAt: number
  /** When the last request started, answered or not; `lastFetchedAt` only moves on success. */
  lastAttemptAt: number
  /** Bumped whenever the answer changes, so route guards can re-check. */
  version: number
  /** Load the answer for this company (or settle on "none" when there is no company). */
  ensureCompany: (companyId: string | null) => Promise<void>
  /** Ask again for the current company, e.g. after an access change. Keeps the old answer on failure. */
  refresh: (opts?: { maxAgeMs?: number }) => Promise<void>
  ensureCatalogue: () => Promise<void>
  clear: () => void
}

const INITIAL = {
  status: 'idle' as RbacStatus,
  userId: null as string | null,
  companyId: null as string | null,
  me: null as RbacMe | null,
  lastFetchedAt: 0,
  lastAttemptAt: 0,
}

/**
 * Shortest gap between two requests that nobody explicitly asked for (window focus, a remount)
 * after a failure. Each `/api/rbac/me` costs several Back4App requests, so a failure must not be
 * retried on every focus; 30 s keeps recovery quick while capping a failing window at 2 per minute.
 */
export const RBAC_RETRY_FLOOR_MS = 30_000

/** Settled means the UI can decide: the answer is in, or there is none to wait for. */
export function isRbacSettled(status: RbacStatus): boolean {
  return status === 'ready' || status === 'none' || status === 'error'
}

let inflight: { key: string; promise: Promise<void> } | null = null
let catalogueInflight: Promise<void> | null = null
/** Bumped by clear(); a request started before a clear must not repopulate the store. */
let generation = 0

function currentUserId(): string | null {
  try {
    return Parse.User.current()?.id ?? null
  } catch {
    return null
  }
}

export const useRbacStore = create<RbacState>()(
  subscribeWithSelector((set, get) => {
    const settle = (patch: Partial<RbacState>) =>
      set((state) => ({ ...patch, version: state.version + 1 }))

    const load = (companyId: string, userId: string): Promise<void> => {
      const key = `${userId}:${companyId}`
      if (inflight?.key === key) return inflight.promise

      const startedGeneration = generation
      const previous = get()
      const sameAnswer = previous.userId === userId && previous.companyId === companyId
      // The old answer stays visible while a refresh runs, but another company's never does.
      if (!(sameAnswer && previous.me)) {
        set({ status: 'loading', userId, companyId, me: null })
      }
      set({ lastAttemptAt: Date.now() })
      // Also stale when the person has moved to another company since: that request set the
      // company it is for, so a late answer for company A must not settle company B.
      const stale = () =>
        startedGeneration !== generation ||
        currentUserId() !== userId ||
        get().companyId !== companyId

      const promise = (async () => {
        try {
          const me = await fetchMyAccess(companyId)
          if (stale()) return
          settle({ status: 'ready', userId, companyId, me, lastFetchedAt: Date.now() })
        } catch (error) {
          if (stale()) return
          if (isRbacApiError(error) && error.status === 403 && error.reason === 'not_a_member') {
            settle({ status: 'none', userId, companyId, me: null })
            return
          }
          console.warn('[rbacStore] Permission check failed', error)
          const current = get()
          if (current.me && current.userId === userId && current.companyId === companyId) return
          settle({ status: 'error', userId, companyId, me: null })
        } finally {
          if (inflight?.promise === promise) inflight = null
        }
      })()

      inflight = { key, promise }
      return promise
    }

    return {
      ...INITIAL,
      catalogue: null,
      version: 0,

      ensureCompany: (companyId) => {
        const userId = currentUserId()
        if (!userId) {
          get().clear()
          return Promise.resolve()
        }
        if (!companyId) {
          if (get().status === 'none' && get().userId === userId && get().companyId === null) {
            return Promise.resolve()
          }
          generation += 1
          inflight = null
          settle({ ...INITIAL, status: 'none', userId })
          return Promise.resolve()
        }
        const state = get()
        const sameAnswer = state.userId === userId && state.companyId === companyId
        if (sameAnswer && (state.status === 'ready' || state.status === 'none')) {
          return Promise.resolve()
        }
        // The same company that just failed waits out the floor; another company loads at once.
        if (
          sameAnswer &&
          state.status === 'error' &&
          Date.now() - state.lastAttemptAt < RBAC_RETRY_FLOOR_MS
        ) {
          return Promise.resolve()
        }
        return load(companyId, userId)
      },

      refresh: (opts = {}) => {
        const userId = currentUserId()
        const { companyId, status, lastFetchedAt, lastAttemptAt } = get()
        if (!userId || !companyId) return Promise.resolve()
        // Only callers that pass an age limit are passive (window focus); an explicit refresh
        // after an access change always asks.
        if (opts.maxAgeMs !== undefined) {
          if (status === 'ready' && Date.now() - lastFetchedAt < opts.maxAgeMs) {
            return Promise.resolve()
          }
          // Covers a first load that failed and a refresh that failed over a kept answer; in both
          // `lastFetchedAt` is old, so only the attempt time stops a retry on every focus.
          if (Date.now() - lastAttemptAt < RBAC_RETRY_FLOOR_MS) return Promise.resolve()
        }
        return load(companyId, userId)
      },

      ensureCatalogue: () => {
        if (get().catalogue) return Promise.resolve()
        if (catalogueInflight) return catalogueInflight
        const startedGeneration = generation
        const promise = (async () => {
          try {
            const catalogue = await fetchCatalogue()
            if (startedGeneration !== generation) return
            set({ catalogue })
          } catch (error) {
            // The drawer and the labels fall back to the catalogue compiled into this bundle.
            console.warn('[rbacStore] Catalogue request failed', error)
          } finally {
            if (catalogueInflight === promise) catalogueInflight = null
          }
        })()
        catalogueInflight = promise
        return promise
      },

      clear: () => {
        generation += 1
        inflight = null
        catalogueInflight = null
        settle({ ...INITIAL, catalogue: null })
      },
    }
  })
)

/** Called from `authService.signOut`, next to the other per-user caches. */
export function clearRbacState(): void {
  useRbacStore.getState().clear()
}
